import { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { CartButton } from './cart/CartButton';
import { CartDrawer } from './cart/CartDrawer';
import { Header } from './Header';
import { Toaster } from './ui/Toaster';

/**
 * Root layout. The Header self-conditions on auth state, so the Landing and
 * Login routes still render a clean, header-less page. The cart button +
 * drawer live here so they follow the customer across any page they hit
 * (menu, order tracking, etc.) — the button auto-hides when the cart is
 * empty, so staff routes (/kitchen, /admin) stay clean.
 *
 * Profile-completion guard: a freshly-signed-in teacher with no room
 * number gets bounced to /profile/setup until they fill it out. The
 * guard explicitly skips Landing and Login so the auth flow still works.
 */
export function Layout() {
  const [cartOpen, setCartOpen] = useState(false);
  const location = useLocation();
  const { isAuthed, role, appUser, isLoading } = useAuth();

  const onPublicRoute = location.pathname === '/' || location.pathname === '/login';
  const onProfileSetup = location.pathname === '/profile/setup';
  const needsProfileSetup =
    isAuthed &&
    !isLoading &&
    role === 'teacher' &&
    appUser !== null &&
    (appUser.roomNumber === null || appUser.roomNumber.length === 0);

  if (needsProfileSetup && !onProfileSetup && !onPublicRoute) {
    return <Navigate to="/profile/setup" replace />;
  }

  const onStaffRoute =
    location.pathname.startsWith('/kitchen') || location.pathname.startsWith('/admin');
  // The checkout page is itself a cart view — surfacing the floating cart
  // button and drawer on top of the fixed Place-Order bar is redundant and
  // visually noisy, especially on small screens where they overlap.
  const onCheckoutRoute = location.pathname === '/checkout';
  const showCart = !onStaffRoute && !onCheckoutRoute;

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      {showCart && (
        <>
          <CartButton onClick={() => setCartOpen(true)} />
          <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
        </>
      )}
      <Toaster />
    </div>
  );
}
